import { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Center, Spinner, useDisclosure } from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import { SortingState } from '@tanstack/react-table';

import { Table, IndividualActions } from 'app/components/UI/Table';

import InviteIndividualModal from 'app/components/Individuals/InviteIndividual/InviteIndividualModal';
import DeleteIndividualModal from 'app/components/Individuals/DeleteIndividual/DeleteIndividualModal';
import ManageUnpaidLeaveModal from 'app/components/Employees/ManageUnpaidLeaveModal';
import PauseEmployeeModal from 'app/components/Employees/PauseEmployee/PauseEmployeeModal';
import TerminateEmployeeModal from 'app/components/Employees/TerminateEmployee/TerminateEmployeeModal';
import EditEmployeeTerminationDateModal from 'app/components/Employees/EditEmployeeTerminationDate/EditEmployeeTerminationDateModal';
import ReactivateEmployeeModal from 'app/components/Employees/ReactivateEmployee/ReactivateEmployeeModal';
import RewardPointsModal from 'app/components/Employees/RewardPointsModal';

import useFetchEmployeesPage from 'app/hooks/useFetchEmployeesPage';

import { getIndividualColumns } from 'app/utils/table/individuals';
import { getEmployeeProfilePath } from 'app/utils/routes';

import { PayCalendarEmployeesTabProps } from './types';

function PayCalendarEmployeesTab({
  payCalendarId,
  companyId,
  searchValue,
  pagination: { pageIndex, pageSize },
  onPaginationChange,
}: PayCalendarEmployeesTabProps) {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [employeesActionIds, setEmployeesActionIds] = useState<string[]>([]);

  const {
    isOpen: isInviteEmployeeOpen,
    onOpen: onOpenInviteEmployee,
    onClose: onCloseInviteEmployee,
  } = useDisclosure();

  const {
    isOpen: isPauseEmployeeOpen,
    onOpen: onOpenPauseEmployee,
    onClose: onClosePauseEmployee,
  } = useDisclosure();

  const {
    isOpen: isReactivateEmployeeOpen,
    onOpen: onOpenReactivateEmployee,
    onClose: onCloseReactivateEmployee,
  } = useDisclosure();

  const {
    isOpen: isManageUnpaidLeaveOpen,
    onOpen: onOpenManageUnpaidLeave,
    onClose: onCloseManageUnpaidLeave,
  } = useDisclosure();

  const {
    isOpen: isTerminateEmployeeOpen,
    onOpen: onOpenTerminateEmployee,
    onClose: onCloseTerminateEmployee,
  } = useDisclosure();

  const {
    isOpen: isEditEmployeeTerminationDateOpen,
    onOpen: onOpenEditEmployeeTerminationDate,
    onClose: onCloseEditEmployeeTerminationDate,
  } = useDisclosure();

  const {
    isOpen: isRewardPointsOpen,
    onOpen: onOpenRewardPoints,
    onClose: onCloseRewardPoints,
  } = useDisclosure();

  const {
    isOpen: isDeleteEmployeeOpen,
    onOpen: onOpenDeleteEmployee,
    onClose: onCloseDeleteEmployee,
  } = useDisclosure();

  const { data: employees, isLoading: isLoadingEmployees } = useFetchEmployeesPage({
    companyId: [companyId || ''],
    payCalendarId: [payCalendarId || ''],
    query: searchValue,
    sortBy: sorting[0]?.id,
    sort: !sorting[0] || sorting[0]?.desc ? 'DESC' : 'ASC',
    page: pageIndex,
    size: pageSize,
  });

  const handleEditProfile = (ids: number[]) => {
    navigate(getEmployeeProfilePath(employees?.list[ids[0]]?.id || ''));
  };

  const openModalWithIds = (ids: number[], onOpen: () => void) => {
    setEmployeesActionIds(ids.map((id) => employees?.list[id]?.id || ''));
    onOpen();
  };

  const handleInvalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['fetchEmployeesPage'] });
    queryClient.invalidateQueries({ queryKey: ['fetchEmployeesStats'] });

    setEmployeesActionIds([]);
  };

  const actions: IndividualActions = {
    edit: handleEditProfile,
    invite: (ids) => openModalWithIds(ids, onOpenInviteEmployee),
    pause: (ids) => openModalWithIds(ids, onOpenPauseEmployee),
    reactivate: (ids) => openModalWithIds(ids, onOpenReactivateEmployee),
    setUnpaid: (ids) => openModalWithIds(ids, onOpenManageUnpaidLeave),
    removeUnpaid: (ids) => openModalWithIds(ids, onOpenManageUnpaidLeave),
    terminate: (ids) => openModalWithIds(ids, onOpenTerminateEmployee),
    editTerminate: (ids) => openModalWithIds(ids, onOpenEditEmployeeTerminationDate),
    rewardPoints: (ids) => openModalWithIds(ids, onOpenRewardPoints),
    delete: (ids) => openModalWithIds(ids, onOpenDeleteEmployee),
  };

  return (
    <>
      <InviteIndividualModal
        selectedIndividuals={employeesActionIds}
        isOpen={isInviteEmployeeOpen}
        onClose={onCloseInviteEmployee}
        onInvite={handleInvalidateQueries}
        type="employee"
      />
      <PauseEmployeeModal
        id={employeesActionIds[0]}
        isOpen={isPauseEmployeeOpen}
        onClose={onClosePauseEmployee}
        onPause={handleInvalidateQueries}
      />
      <ReactivateEmployeeModal
        id={employeesActionIds[0]}
        isOpen={isReactivateEmployeeOpen}
        onClose={onCloseReactivateEmployee}
        onReactivate={handleInvalidateQueries}
      />
      <ManageUnpaidLeaveModal
        id={employeesActionIds[0]}
        isOpen={isManageUnpaidLeaveOpen}
        onClose={onCloseManageUnpaidLeave}
        onUnpaidLeave={handleInvalidateQueries}
      />
      <TerminateEmployeeModal
        id={employeesActionIds[0]}
        isOpen={isTerminateEmployeeOpen}
        onClose={onCloseTerminateEmployee}
        onTerminate={handleInvalidateQueries}
      />
      <EditEmployeeTerminationDateModal
        id={employeesActionIds[0]}
        isOpen={isEditEmployeeTerminationDateOpen}
        onClose={onCloseEditEmployeeTerminationDate}
        onEditTerminate={handleInvalidateQueries}
      />
      <RewardPointsModal
        id={employeesActionIds[0]}
        isOpen={isRewardPointsOpen}
        onClose={onCloseRewardPoints}
        onRewardPoints={handleInvalidateQueries}
      />
      <DeleteIndividualModal
        selectedIndividuals={employeesActionIds}
        isOpen={isDeleteEmployeeOpen}
        onClose={onCloseDeleteEmployee}
        onDelete={handleInvalidateQueries}
        type="employee"
      />

      {isLoadingEmployees ? (
        <Center width="full" height="full" flexGrow={1}>
          <Spinner color="brand.purple" size="xl" mb="50px" />
        </Center>
      ) : (
        <Table
          columns={getIndividualColumns('employee', actions, false)}
          data={employees?.list || []}
          pageNumber={employees?.pageNumber || pageIndex}
          pageSize={employees?.pageSize || pageSize}
          total={employees?.total || 0}
          sorting={sorting}
          onPaginationChange={onPaginationChange}
          onSortingChange={setSorting}
        />
      )}
    </>
  );
}

export default PayCalendarEmployeesTab;
